/* Hallmark · macrostructure: Form Page (app) · tone: plain · theme: console-system */
import { useDirtyConfirm } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  DirtyConfirmDialog,
  Input,
  Label,
  ReadOnlyBanner,
  Skeleton,
  toast,
  useDirtyTracker,
} from '@xhubio-saas/core-frontend/ui-components'
import { Building2, Loader2 } from 'lucide-react'
import { type FormEvent, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  missingSellerRequirements,
  SELLER_PROFILE_LIMITS,
  type SellerProfile,
} from '@/api/sellerProfileClient'

/**
 * **Firmendaten** (Plan API 2026-11-01/01, E14/E15) — der Absender jeder E-Rechnung, die „E-Rechnung aus PDF"
 * erzeugt. Gespeichert unter `organization.metadata.invoiceApiSeller` ueber `sellerProfile.update`.
 *
 * - Formular-Page nach UI_GUIDELINES §9 (Spalte 880 px, Sektionen als Cards, Feldbreite = Eingabelaenge, Sticky
 *   Action-Bar). Speichern nur bei echtem Wertunterschied (`formular-echte-aenderung.md`): `useDirtyTracker` mit
 *   `initial` = gespeicherter Stand, Knopf `!dirty || saving`, `if (!dirty) return`, nach Erfolg `reset(gesendet)`.
 * - 🔴 Noch nie gespeichert: die Werte kommen aus den Organisationsdaten (Vorbelegung E14 — legalName, Anschrift,
 *   USt-IdNr., Steuernummer, Telefon, E-Mail), die Baseline bleibt **leer**. Die Vorbelegung ist damit ein echter
 *   Unterschied zum Serverstand und laesst sich speichern; sie wird nie zur Baseline erklaert.
 * - Schreiben duerfen Owner/Admin (E14, P1-16); alle anderen sehen dieselbe Seite gesperrt, mit Grund.
 */

export interface SellerFormValues {
  name: string
  street: string
  postalCode: string
  city: string
  countryCode: string
  vatId: string
  taxId: string
  iban: string
  bic: string
  bankName: string
  contactName: string
  contactPhone: string
  contactEmail: string
}

type FieldName = keyof SellerFormValues

const LIMIT: Record<FieldName, number> = SELLER_PROFILE_LIMITS

const s = (v: string | null | undefined) => (typeof v === 'string' ? v : '')

export function toSellerFormValues(p: SellerProfile | null): SellerFormValues {
  return {
    name: s(p?.name),
    street: s(p?.street),
    postalCode: s(p?.postalCode),
    city: s(p?.city),
    countryCode: s(p?.countryCode),
    vatId: s(p?.vatId),
    taxId: s(p?.taxId),
    iban: s(p?.bankAccount?.iban),
    bic: s(p?.bankAccount?.bic),
    bankName: s(p?.bankAccount?.bankName),
    contactName: s(p?.contact?.name),
    contactPhone: s(p?.contact?.phone),
    contactEmail: s(p?.contact?.email),
  }
}

/** Formular → `seller`-Block. Leere Felder reisen nicht (das Server-Schema entscheidet, was Pflicht ist). */
export function fromSellerFormValues(v: SellerFormValues): SellerProfile {
  const opt = (x: string) => {
    const trimmed = x.trim()
    return trimmed === '' ? undefined : trimmed
  }
  const bankAccount = {
    iban: opt(v.iban.replace(/\s+/g, '').toUpperCase()),
    bic: opt(v.bic.replace(/\s+/g, '').toUpperCase()),
    bankName: opt(v.bankName),
  }
  const contact = {
    name: opt(v.contactName),
    phone: opt(v.contactPhone),
    email: opt(v.contactEmail),
  }
  const some = (o: Record<string, string | undefined>) => Object.values(o).some(Boolean)
  return {
    name: opt(v.name),
    street: opt(v.street),
    postalCode: opt(v.postalCode),
    city: opt(v.city),
    countryCode: opt(v.countryCode.toUpperCase()),
    vatId: opt(v.vatId),
    taxId: opt(v.taxId),
    ...(some(bankAccount) ? { bankAccount } : {}),
    ...(some(contact) ? { contact } : {}),
  }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const IBAN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/

/** Format-Pruefung beim Speichern (Laengen begrenzt bereits `maxLength`). */
function validate(v: SellerFormValues): Partial<Record<FieldName, string>> {
  const errors: Partial<Record<FieldName, string>> = {}
  if (v.contactEmail.trim() !== '' && !EMAIL.test(v.contactEmail.trim())) {
    errors.contactEmail = 'sellerProfile.error.email'
  }
  const iban = v.iban.replace(/\s+/g, '').toUpperCase()
  if (iban !== '' && !IBAN.test(iban)) errors.iban = 'sellerProfile.error.iban'
  if (v.countryCode.trim() !== '' && !/^[A-Za-z]{2}$/.test(v.countryCode.trim())) {
    errors.countryCode = 'sellerProfile.error.countryCode'
  }
  return errors
}

export interface SellerProfilePageProps {
  /** Der gespeicherte Stand — `null`, solange noch nie gespeichert wurde. */
  profile: SellerProfile | null
  /** Vorbelegung aus den Organisationsdaten, nur wenn `profile` `null` ist. */
  prefill?: SellerProfile | null
  loading?: boolean
  /** Die Firmendaten liessen sich nicht laden — dann kein Formular (Speichern ueberschriebe Unbekanntes). */
  loadError?: string | null
  canEdit: boolean
  onSave: (profile: SellerProfile) => Promise<unknown>
}

export function SellerProfilePage({
  loading = false,
  loadError = null,
  ...rest
}: SellerProfilePageProps) {
  const { t } = useTranslation()
  return (
    <div className="container mx-auto space-y-6 px-4 py-6" data-testid="seller-profile-page">
      <div className="mx-auto max-w-[880px] space-y-1">
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
          <h1 className="text-3xl tracking-tight">{t('sellerProfile.page.title')}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{t('sellerProfile.page.subtitle')}</p>
      </div>
      {/* Das Formular erst, wenn beide Quellen da sind: seine Werte werden einmal gesetzt (Vorbelegung oder
          gespeicherter Stand), die Baseline traegt `initial`. */}
      {loading ? (
        <div className="mx-auto max-w-[880px] space-y-3" data-testid="seller-profile-loading">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : loadError !== null ? (
        <p
          className="mx-auto max-w-[880px] rounded-lg border border-destructive/40 p-4 text-sm text-destructive"
          role="alert"
          data-testid="seller-profile-load-error"
        >
          {t('sellerProfile.loadFailed')}
          {loadError ? ` (${loadError})` : ''}
        </p>
      ) : (
        <SellerProfileForm {...rest} />
      )}
    </div>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section className="rounded-xl border border-[var(--border-subtle)] bg-card shadow-sm">
      <div className="space-y-0.5 px-6 pt-5">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-12 gap-4 p-6">{children}</div>
    </section>
  )
}

function SellerProfileForm({
  profile,
  prefill = null,
  canEdit,
  onSave,
}: Omit<SellerProfilePageProps, 'loading' | 'loadError'>) {
  const { t } = useTranslation()
  const initial = toSellerFormValues(profile)
  const [values, setValues] = useState<SellerFormValues>(() =>
    profile ? toSellerFormValues(profile) : toSellerFormValues(prefill)
  )
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
  const [saving, setSaving] = useState(false)

  const { dirty, reset, discard } = useDirtyTracker(values, {
    initial,
    onAdopt: setValues,
    onDiscard: setValues,
  })
  const { blocker } = useDirtyConfirm({ enabled: dirty && !saving })

  const missing = missingSellerRequirements(fromSellerFormValues(values))

  const set = (field: FieldName) => (e: { target: { value: string } }) => {
    const value = e.target.value
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    // 🔴 Kein Speichern-Aufruf ohne echte Aenderung — der gesperrte Knopf allein reicht nicht (Enter im Feld).
    if (!dirty || saving || !canEdit) return
    const found = validate(values)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    const sent = values
    setSaving(true)
    try {
      await onSave(fromSellerFormValues(sent))
      reset(sent)
      toast.success(t('sellerProfile.saved'))
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t('sellerProfile.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const field = (
    name: FieldName,
    span: string,
    opts: { type?: 'text' | 'email' | 'tel'; optional?: boolean; hint?: string } = {}
  ) => {
    const id = `seller-profile-${name}`
    const error = errors[name]
    return (
      <div className={`col-span-12 space-y-2 ${span}`}>
        <Label htmlFor={id}>
          {t(`sellerProfile.field.${name}`)}
          {opts.optional && (
            <span className="font-normal text-muted-foreground">
              {' '}
              {t('sellerProfile.optional')}
            </span>
          )}
        </Label>
        <Input
          id={id}
          type={opts.type ?? 'text'}
          value={values[name]}
          maxLength={LIMIT[name]}
          onChange={set(name)}
          disabled={!canEdit || saving}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          data-error={error ? 'true' : undefined}
          className={`h-9 ${error ? 'border-[var(--destructive)] ring-1 ring-[var(--destructive)]' : ''}`}
        />
        {opts.hint && !error && <p className="text-xs text-muted-foreground">{opts.hint}</p>}
        {error && (
          <p id={`${id}-error`} className="text-sm text-destructive">
            {t(error)}
          </p>
        )}
      </div>
    )
  }

  return (
    <form
      noValidate
      onSubmit={(e) => void submit(e)}
      className="mx-auto max-w-[880px] space-y-6"
      data-testid="seller-profile-form"
    >
      {!canEdit && (
        <ReadOnlyBanner
          reason="permission-missing"
          message={t('sellerProfile.readOnly')}
          data-testid="seller-profile-readonly"
        />
      )}
      {missing.length > 0 ? (
        <p
          className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="seller-profile-incomplete"
        >
          {t('sellerProfile.incomplete', {
            fields: missing.map((m) => t(`sellerProfile.requirement.${m}`)).join(', '),
          })}
        </p>
      ) : (
        <p
          className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
          data-testid="seller-profile-complete"
        >
          {t('sellerProfile.complete')}
        </p>
      )}

      <Section
        title={t('sellerProfile.section.company.title')}
        description={t('sellerProfile.section.company.description')}
      >
        {field('name', 'sm:col-span-12')}
        {field('street', 'sm:col-span-8')}
        {field('postalCode', 'sm:col-span-3')}
        {field('city', 'sm:col-span-6')}
        {field('countryCode', 'sm:col-span-3', { hint: t('sellerProfile.hint.countryCode') })}
      </Section>

      <Section
        title={t('sellerProfile.section.tax.title')}
        description={t('sellerProfile.section.tax.description')}
      >
        {field('vatId', 'sm:col-span-6')}
        {field('taxId', 'sm:col-span-6')}
      </Section>

      <Section
        title={t('sellerProfile.section.bank.title')}
        description={t('sellerProfile.section.bank.description')}
      >
        {field('iban', 'sm:col-span-8')}
        {field('bic', 'sm:col-span-4', { optional: true })}
        {field('bankName', 'sm:col-span-8', { optional: true })}
      </Section>

      <Section
        title={t('sellerProfile.section.contact.title')}
        description={t('sellerProfile.section.contact.description')}
      >
        {field('contactName', 'sm:col-span-8', { optional: true })}
        {field('contactPhone', 'sm:col-span-6', { type: 'tel' })}
        {field('contactEmail', 'sm:col-span-6', { type: 'email' })}
      </Section>

      {canEdit && (
        <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-3 border-t bg-background/80 px-4 py-3 backdrop-blur">
          {dirty && (
            <Button type="button" variant="ghost" onClick={discard} disabled={saving}>
              {t('sellerProfile.discard')}
            </Button>
          )}
          <Button type="submit" disabled={!dirty || saving} data-testid="seller-profile-save">
            {saving && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      )}

      <DirtyConfirmDialog
        blocker={blocker as unknown as Parameters<typeof DirtyConfirmDialog>[0]['blocker']}
      />
    </form>
  )
}
